import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, Navigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useRouter } from './use-router';
import { RootState } from '../store/store';
import { isLoginFunc } from '../store/slices/login';
import { useRefreshtokenMutation, useLogoutMutation } from '../pages/login/services/authenticationApi';
import { clearPermissionCache } from '../hooks/usePermission';
import { globalVal } from '../utils/global-values';

const REFRESH_BLOCK_KEY = 'refreshBlockUntil';
const REFRESH_BLOCK_DURATION = 10000; // 10 saniye
const LOGOUT_REQUEST_BUFFER_SECONDS = 10;
// EWAP-1000: Aktif kullanıcının oturumu, bitişine bu kadar süre kala sessizce yenilenir.
// İnaktif kullanıcı ise yenilenmez; token bitiminde (exp) doğrudan çıkışa yönlendirilir.
const SESSION_REFRESH_BEFORE_EXP_SECONDS = 70;
const refreshChannel = new BroadcastChannel('auth-refresh');

let timerId: ReturnType<typeof setInterval> | null = null;

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { refreshExpired } = useSelector((state: RootState) => state.refreshExpired);
  const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
  const sessionToken = localStorage.getItem('accessToken');
  const location = useLocation();
  const router = useRouter();
  const dispatch = useDispatch();

  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [timeLeft, setTimeLeft] = useState(60000);

  const [refreshTokenMutation] = useRefreshtokenMutation();
  const [logout] = useLogoutMutation();
  const userActivityTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef(false);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);
  const isLoggingOutRef = useRef(false);
  const continueFncRef = useRef<() => Promise<void>>(async () => {});
  const timerControlRef = useRef<() => void>(() => {});

  const mealCardRoutes = ['/cards/bulk-meal-card', '/cards/meal-allowance'];
  const isAccessingMealCardPage = mealCardRoutes.includes(location.pathname);
  const shouldBlockMealCardAccess =
    isAccessingMealCardPage &&
    (currentUserState?.isUsedMealCard === null || currentUserState?.isUsedMealCard === false);

  const logOutFnc = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    logout();
    clearPermissionCache();
    globalVal.isLoggedOut = true;
    dispatch(isLoginFunc(false));
    localStorage.clear();
    router.push('/login');
  }, [logout, dispatch, router]);

  function isRefreshBlocked() {
    const blockUntil = localStorage.getItem(REFRESH_BLOCK_KEY);
    if (!blockUntil) return false;
    return Date.now() < Number(blockUntil);
  }

  const timerControl = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const tokenExpirationTime = tokenPayload.exp;
        const currentTime = Math.floor(Date.now() / 1000);
        let remaining = tokenExpirationTime - currentTime;
        const adjustedRemaining = Math.max(remaining - LOGOUT_REQUEST_BUFFER_SECONDS, 0);
        setTimeLeft(adjustedRemaining);
        if (timerId) clearInterval(timerId);
        if (!isRefreshingRef.current && remaining <= LOGOUT_REQUEST_BUFFER_SECONDS) {
          logOutFnc();
          return;
        }
        timerId = setInterval(() => {
          setTimeLeft((prevTime) => {
            if (isRefreshingRef.current) return prevTime;
            if (prevTime <= 0) {
              logOutFnc();
              if (timerId) clearInterval(timerId);
              return 0;
            }
            const sessionToken = localStorage.getItem('accessToken');
            if (sessionToken === null) {
              if (timerId) clearInterval(timerId);
              return 0;
            }
            try {
              const tokenPayload = JSON.parse(atob(sessionToken.split('.')[1]));
              const currentTime = Math.floor(Date.now() / 1000);
              const tokenExpirationTime = tokenPayload.exp;
              const remaining = tokenExpirationTime - currentTime;
              const userActive = localStorage.getItem('isUserActive') === 'true';
              const adjustedRemaining = Math.max(remaining - LOGOUT_REQUEST_BUFFER_SECONDS, 0);
              // EWAP-1000: Token bitiminde (buffer kadar kala) pop-up gösterilmeden
              // doğrudan çıkış yapılır. İnaktif kullanıcı da buraya düşerek tam bitişte atılır.
              if (!isRefreshingRef.current && remaining <= LOGOUT_REQUEST_BUFFER_SECONDS) {
                logOutFnc();
                if (timerId) clearInterval(timerId);
                return 0;
              }
              // Sadece aktif kullanıcı için oturum, bitmeden sessizce yenilenir. İnaktif
              // kullanıcı için hiçbir işlem yapılmaz; token exp'e kadar sayar ve yukarıdaki
              // kontrolle tam bitişte doğrudan çıkışa yönlendirilir.
              if (userActive && remaining <= SESSION_REFRESH_BEFORE_EXP_SECONDS) {
                continueFncRef.current();
              }
              return adjustedRemaining;
            } catch {
              return prevTime - 1;
            }
          });
        }, 1000);
      } catch {
        logOutFnc();
      }
    }
  }, [logOutFnc]);

  const continueFnc = useCallback(async () => {
    if (isRefreshBlocked()) return;
    if (isRefreshingRef.current && refreshPromiseRef.current) {
      await refreshPromiseRef.current;
      return;
    }
    isRefreshingRef.current = true;
    localStorage.setItem(REFRESH_BLOCK_KEY, String(Date.now() + REFRESH_BLOCK_DURATION));
    refreshChannel.postMessage({ type: 'refreshing' });
    const promise = (async () => {
      try {
        if (timerId) clearInterval(timerId);
        const refreshTokenFromStorage = localStorage.getItem('refreshToken');
        const response = await refreshTokenMutation({ refreshToken: refreshTokenFromStorage }).unwrap();
        const data = response as { success?: boolean; data?: { token: string; refreshToken: string } };
        if (data?.success && data?.data?.token) {
          localStorage.setItem('accessToken', data.data.token);
          localStorage.setItem('refreshToken', data.data.refreshToken);
          localStorage.setItem('isUserActive', 'false');
          timerControlRef.current();
          refreshChannel.postMessage({ type: 'token-updated', token: data.data.token });
        } else {
          logOutFnc();
        }
      } catch {
        logOutFnc();
      } finally {
        isRefreshingRef.current = false;
        refreshPromiseRef.current = null;
        refreshChannel.postMessage({ type: 'refresh-done' });
      }
    })();
    refreshPromiseRef.current = promise;
    await promise;
  }, [refreshTokenMutation, logOutFnc]);

  continueFncRef.current = continueFnc;
  timerControlRef.current = timerControl;

  useEffect(() => {
    refreshChannel.onmessage = (event: MessageEvent<{ type: string; token?: string }>) => {
      if (event.data.type === 'token-updated' && event.data.token) {
        localStorage.setItem('accessToken', event.data.token);
      }
      if (event.data.type === 'refreshing') {
        isRefreshingRef.current = true;
      }
      if (event.data.type === 'refresh-done') {
        isRefreshingRef.current = false;
      }
    };
    return () => {
      refreshChannel.onmessage = null;
    };
  }, []);

  const handleUserActivity = useCallback(() => {
    localStorage.setItem('isUserActive', 'true');
    if (userActivityTimeout.current) clearTimeout(userActivityTimeout.current);
    userActivityTimeout.current = setTimeout(() => {
      localStorage.setItem('isUserActive', 'false');
    }, 120000);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);
    return () => {
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
      if (userActivityTimeout.current) clearTimeout(userActivityTimeout.current);
    };
  }, [handleUserActivity]);

  useEffect(() => {
    const checkTokenValidity = () => {
      if (!sessionToken) {
        setIsTokenValid(false);
        router.push('/login');
        return;
      }
      try {
        const tokenPayload = JSON.parse(atob(sessionToken.split('.')[1]));
        const currentTime = Date.now() / 1000;
        dispatch(isLoginFunc(true));
        setIsTokenValid(tokenPayload.exp > currentTime);
      } catch {
        setIsTokenValid(false);
      }
    };
    checkTokenValidity();
  }, [sessionToken, dispatch, router]);

  useEffect(() => {
    if (isTokenValid === false) {
      clearPermissionCache();
      globalVal.isLoggedOut = true;
      dispatch(isLoginFunc(false));
      localStorage.clear();
      router.push('/login');
    } else if (isTokenValid === true && location.pathname === '/login') {
      router.back();
    }
  }, [isTokenValid, location.pathname, router]);

  useEffect(() => {
    if (isRefreshingRef.current) return;
    if (timeLeft <= 0) {
      logOutFnc();
      if (timerId) clearInterval(timerId);
    }
  }, [timeLeft, logOutFnc]);

  useEffect(() => {
    timerControl();
    document.addEventListener('visibilitychange', timerControl);
    document.addEventListener('click', timerControl);
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', timerControl);
    }
    return () => {
      document.removeEventListener('visibilitychange', timerControl);
      document.removeEventListener('click', timerControl);
      if (timerId) clearInterval(timerId);
      if (navigator.serviceWorker) {
        navigator.serviceWorker.removeEventListener('message', timerControl);
      }
    };
  }, [refreshExpired, timerControl]);

  if (isTokenValid === null || !isTokenValid) {
    return (
      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: 'white' }} />
      </div>
    );
  }

  if (shouldBlockMealCardAccess) {
    return <Navigate to="/dashboard/transactions" replace />;
  }

  return (
    <div
      style={{
        width: '100%',
        position: 'relative',
        backgroundColor: '#f4f5f7',
      }}
    >
      {children}
    </div>
  );
};

export default AuthGuard;
